import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Table, Button, Input, Space, Typography, InputNumber } from 'antd';
import { useGetPrimaveraTasksSmall } from '../../hooks/genericHooks';
import type { PrimaveraTaskSmall } from '../../types';
import WbsSelectModal from './WbsSelectModal';

interface ActivitySelectionsProps {
  open: boolean;
  value?: string;
  onOk: (selected: string | [string, string, string] | [string, string, string, number]) => void;
  onCancel: () => void;
  title?: string;
  filters?: { [key: string]: any };
}

interface ActivitySelectionsContentProps {
  value?: string;
  onOk: (selected: string | [string, string, string] | [string, string, string, number]) => void;
  onCancel: () => void;
  filters?: { [key: string]: any };
  onWbsChange?: (wbs: any | null) => void;
}

const ActivitySelectionsContent: React.FC<ActivitySelectionsContentProps> = ({ value, onOk, onCancel, filters, onWbsChange }) => {
  const { data: allTasks, isLoading } = useGetPrimaveraTasksSmall();
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(value ?? null);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [wbsModalOpen, setWbsModalOpen] = useState(false);
  const [selectedWbs, setSelectedWbs] = useState<any>(null);

  useEffect(() => {
    setSelectedRowKey(value ?? null);
  }, [value]);

  const filteredData = useMemo(() => {
    let data = allTasks?.data || [];
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        data = data.filter((item: any) => item[k] === v);
      });
    }
    if (selectedWbs && selectedWbs.wbs_id) {
      data = data.filter((item: PrimaveraTaskSmall) => item.wbs_id === selectedWbs.wbs_id);
    }
    if (search) {
      const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
      data = data.filter((item: PrimaveraTaskSmall) => {
        const haystack = `${item.task_code ?? ''} ${item.task_name ?? ''} ${item.wbs_id ?? ''} ${item.task_id ?? ''}`.toLowerCase();
        return tokens.every(t => haystack.includes(t));
      });
    }
    return data;
  }, [allTasks, filters, search, selectedWbs]);

  const columns = [
    { title: 'Task Code', dataIndex: 'task_code', key: 'task_code' },
    { title: 'Task Name', dataIndex: 'task_name', key: 'task_name' },
    { title: 'WBS ID', dataIndex: 'wbs_id', key: 'wbs_id' },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Button onClick={() => setWbsModalOpen(true)} style={{ marginBottom: 8 }}>
        Filter by WBS
      </Button>
      {selectedWbs && (
        <Typography.Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
          Selected WBS: {selectedWbs.wbs_name} (ID: {selectedWbs.wbs_id})
          <Button
            size="small"
            style={{ marginLeft: 8 }}
            onClick={() => {
              setSelectedWbs(null);
              onWbsChange && onWbsChange(null);
            }}
          >
            Clear
          </Button>
        </Typography.Text>
      )}
      <Input.Search
        placeholder="Search activities..."
        allowClear
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <Table
        rowKey={record => String(record.task_id)}
        loading={isLoading}
        dataSource={filteredData}
        columns={columns}
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onShowSizeChange: (_current, size) => setPageSize(size),
        }}
        onRow={record => ({
          onClick: (e: React.MouseEvent) => {
            setSelectedRowKey(String(record.task_id));
          },
          onDoubleClick: (e: React.MouseEvent) => {
            setSelectedRowKey(String(record.task_id));
            const code = String(record.task_code ?? '').trim();
            const name = String(record.task_name ?? '').trim();
            const combined = `${code} - ${name}`.trim();
            onOk([combined, code, name, Number(record.task_id)]);
            onCancel();
          },
          style: {
            cursor: 'pointer',
            background: selectedRowKey === String(record.task_id) ? '#e6f7ff' : undefined
          }
        })}
        components={{
          body: {
            row: (props: any) => <tr {...props} style={{ ...props.style, lineHeight: 1, padding: 0, borderBottom: '1px solid #f0f0f0', height: 28 }} />,
            cell: (props: any) => <td {...props} style={{ ...props.style, padding: '0 8px', borderBottom: 'none', height: 28, verticalAlign: 'middle' }} />
          }
        }}
        style={{ margin: 0 }}
        showHeader={true}
        bordered={false}
      />
      <WbsSelectModal
        open={wbsModalOpen}
        onOk={wbs => {
          setSelectedWbs(wbs);
          onWbsChange && onWbsChange(wbs);
          setWbsModalOpen(false);
        }}
        onCancel={() => setWbsModalOpen(false)}
      />
    </Space>
  );
};

const ActivitySelections: React.FC<ActivitySelectionsProps> = ({ open, value, onOk, onCancel, title = 'Select Activity', filters }) => {
  const WIDTH_STORAGE_KEY = 'ActivitySelections:width';
  const DEFAULT_WIDTH = 700;
  const MIN_WIDTH = 520;
  const MAX_WIDTH = 1400;

  // We need access to the count for footer; recompute here by mirroring child logic minimally
  const { data: allTasks } = useGetPrimaveraTasksSmall();
  const [selectedWbs, setSelectedWbs] = useState<any>(null);
  const [modalWidth, setModalWidth] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
      const parsed = raw ? Number(raw) : NaN;
      if (Number.isFinite(parsed)) {
        return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parsed));
      }
    } catch {
      // ignore
    }
    return DEFAULT_WIDTH;
  });
  // Count derived from selectedWbs
  const activitiesCount = useMemo(() => {
    if (!selectedWbs?.wbs_id || !Array.isArray(allTasks?.data)) return 0;
    const wbsId = String(selectedWbs.wbs_id);
    return (allTasks!.data as PrimaveraTaskSmall[]).filter(a => String(a.wbs_id ?? '') === wbsId).length;
  }, [selectedWbs, allTasks]);

  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(modalWidth));
    } catch {
      // ignore
    }
  }, [modalWidth]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={title}
      destroyOnClose
      width={modalWidth}
      footer={[
        selectedWbs?.wbs_id ? (
          <Typography.Text key="count" type="secondary" style={{ marginRight: 'auto' }}>
            {activitiesCount} activities
          </Typography.Text>
        ) : null,
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        // OK is handled by row double-click or selection in this modal; keep no primary action here
      ]}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Space size={8} align="center">
          <Typography.Text type="secondary">Width</Typography.Text>
          <InputNumber
            min={MIN_WIDTH}
            max={MAX_WIDTH}
            step={20}
            value={modalWidth}
            onChange={v => {
              const n = typeof v === 'number' ? v : DEFAULT_WIDTH;
              setModalWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, n)));
            }}
            style={{ width: 110 }}
          />
          <Button
            size="small"
            onClick={() => setModalWidth(DEFAULT_WIDTH)}
            disabled={modalWidth === DEFAULT_WIDTH}
          >
            Reset
          </Button>
        </Space>
      </div>
      <ActivitySelectionsContent
        value={typeof value === 'string' ? value : ''}
        onOk={onOk}
        onCancel={onCancel}
        filters={filters}
        onWbsChange={setSelectedWbs}
      />
    </Modal>
  );
};

export default ActivitySelections;
export { ActivitySelectionsContent };
